import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { PageHero } from "../components/shared/Primitives";
import InstallCareRooms from "../components/shared/InstallCareRooms";

const STATUS_COLOR = {
  discussion: "bg-teal-50 text-teal",
  activated: "bg-purple/10 text-purple",
  expired: "bg-slate-100 text-ink-muted",
  archived: "bg-slate-100 text-ink-muted",
};

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function CareDiscussionRoomList() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/api/care-rooms/mine")
      .then((r) => setRooms(r.data.rooms || []))
      .catch(() => setRooms([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="page-cdr-list">
      <PageHero
        eyebrow="Care Discussion Rooms"
        title="My Care Discussion Rooms"
        description="Private discussion spaces you've created or been invited to."
      >
        <div className="mt-6">
          <Link to="/care-discussion-room/new" className="btn-teal" data-testid="cdr-new-link">
            <Plus className="w-4 h-4" /> Create new room
          </Link>
        </div>
      </PageHero>

      <section className="section pt-12">
        <div className="container-jcc max-w-3xl">
          <InstallCareRooms />
          {loading ? (
            <div className="py-20 text-center text-ink-muted">Loading…</div>
          ) : rooms.length === 0 ? (
            <div className="card-base p-8 text-center" data-testid="cdr-empty">
              <h3 className="font-display text-xl font-semibold">No rooms yet</h3>
              <p className="mt-2 text-ink-muted text-sm">
                Create your first Care Discussion Room to start an agreement conversation.
              </p>
              <Link to="/care-discussion-room/new" className="btn-teal mt-5 inline-flex">
                <Plus className="w-4 h-4" /> Create a room
              </Link>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="cdr-room-list">
              {rooms.map((r) => (
                <li key={r.room_id}>
                  <Link
                    to={`/care-discussion-room/${r.room_id}`}
                    className="block card-base p-5 hover:border-teal/40 transition-colors group"
                    data-testid={`cdr-room-${r.room_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className={`pill ${STATUS_COLOR[r.status] || "bg-slate-100 text-ink-muted"}`}>
                            {r.status}
                          </span>
                          {r.proposed_start_date && (
                            <span className="text-xs text-ink-soft">start: {r.proposed_start_date}</span>
                          )}
                        </div>
                        <h3 className="font-display text-lg font-semibold truncate">
                          {r.client_label}
                        </h3>
                        <p className="text-sm text-ink-muted truncate">
                          Carer: {r.carer_email} · Family: {r.family_email}
                        </p>
                        <p className="text-xs text-ink-soft mt-1">
                          Created {formatDate(r.created_at)} · Expires {formatDate(r.expires_at)}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-ink-soft group-hover:text-teal mt-1" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
